import React from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Link, Navigate } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import Login from './components/Login';
import Register from './components/Register'
import Dashboard from './components/Dashboard/Dashboard';
import Antrag from './components/Antrag/Antrag';
import HomePage from './components/Homepage/Homepage';
import Activation from './components/Activation';
import ActivateUser from './components/ActivateUser';
import NotFound from './components/NotFound';
import PaymentResult from './components/Antrag/Checkout/PaymentResult';
import SetAgencyUserPassword from './components/SetAgencyUserPassword';
import Korrespondenz from './components/Dashboard/Korrespondenz';
import './App.css';
import { AuthProvider } from './AuthContext';
import Modal from 'react-modal';
import imcLogo from './images/logo-imc-fh-krems-signet-square.svg';
import { FaInstagram, FaFacebookF, FaYoutube, FaLinkedin } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import ProtectedRoute from './ProtectedRoute';
import { Flip, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Faq from './components/Faq';

Modal.setAppElement('#root');

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <Header />
        <Main />
        <Footer />
      </Router>
    </AuthProvider>
  );
};

const Header: React.FC = () => {

  return (
    <nav className="navbar navbar-dark navbar-expand shadow">

      <Link to="/" className="navbar-brand logo" >
        <img width="90" src={imcLogo} className="logo-img" alt="Nostrifizierung" />
        <span className="logo-text">Nostrifizierung</span>
      </Link>

    </nav>
  );
};

const Footer: React.FC = () => {
  return (
    <footer className="footer">
      <div className="bg-light footer">
        <div className='socials'>
          <div className='container py-2 text-center'>
            <div className='row no-gutters social-links'>
              <div className='col my-2'>
                <a href='https://www.facebook.com/imcfhkrems'>
                  <FaFacebookF className='social' title='Facebook' />
                </a>
              </div>
              <div className='col my-2'>
                <a href='https://www.instagram.com/imckrems/'>
                  <FaInstagram className='social' title='Instagram' />
                </a>
              </div>
              <div className='col my-2'>
                <a href='https://x.com/imckrems'>
                  <FaXTwitter className='social' title='Twitter/X' />
                </a>
              </div>
              <div className='col my-2'>
                <a href='https://www.youtube.com/fhkrems'>
                  <FaYoutube className='social' title='Youtube' />
                </a>
              </div>
              <div className='col my-2'>
                <a href='https://www.linkedin.com/edu/school?id=10184'>
                  <FaLinkedin className='social' title='LinkedIn' />
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="container py-5 text-center">
          <div className="row no-gutters">
            <div className="col-6 col-md my-2">
              <h5>Fragen zur Nostrifizierung?</h5>
              <ul className="list-unstyled text-small">
                <li><a href="mailto:nostrifizierung@imc.ac.at" className="link-secondary">nostrifizierung@imc.ac.at</a></li>
                <li><a href="tel:00432732802310" className="link-secondary" style={{ textDecoration: "unset" }}>+43 2732 802 310</a></li>
              </ul>
            </div>
            <div className="col-6 col-md my-2">
              <h5>Links</h5>
              <ul className="list-unstyled text-small">
                <li>
                  <Link to="/faq" title='frequently asked questions' onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="link-secondary" style={{ textDecoration: "unset" }}>FAQ</Link>
                </li>
                <li>
                  <a target="_blank" rel="noreferrer" className="link-secondary" style={{ textDecoration: "unset" }} href="https://www.imc.ac.at/impressum/">Impressum</a>
                </li>
                <li>
                  <a target="_blank" rel="noreferrer" className="link-secondary" style={{ textDecoration: "unset" }} href="https://www.imc.ac.at/datenschutzerklaerung/">Datenschutz &amp; Rechtliches</a>
                </li>
              </ul>
            </div>
            <div className="col-12 col-md my-2">
              <a target="_blank" rel="noreferrer" href="https://www.imc.ac.at">
                <img width="48" src={imcLogo} className="img-fluid rounded" alt="https://www.imc.ac.at" />
              </a>
              <a target="_blank" rel="noreferrer" className="link-secondary" style={{ textDecoration: "unset" }} href="https://www.imc.ac.at">
                <div className="small">IMC Krems University of Applied Sciences</div>
                <div className="small">©{new Date().getFullYear()}</div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Main: React.FC = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname.startsWith('/agencyUser/activate/');

  return (
    <div className={isHomePage || isAuthPage ? 'app-container homepage-background' : 'app-container dashboard-background'}>
      <CSSTransition
        key={location.key}
        classNames="fade"
        timeout={300}
      >
        <Routes location={location}>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path='/register' element={<Register />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/korrespondenz" element={
            <ProtectedRoute>
              <Korrespondenz />
            </ProtectedRoute>
          } />
          <Route path='/antrag' element={
            <ProtectedRoute>
              <Antrag />
            </ProtectedRoute>} />
          <Route path='/activation' element={<Activation />} />
          <Route path="/user/activate/:actionGuid" element={<ActivateUser />} />
          <Route path="/agencyUser/activate/:actionGuid" element={<SetAgencyUserPassword />} />

          <Route path="/application/init" element={<PaymentResult />} />

          <Route path="404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </CSSTransition>
      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        draggable
        pauseOnHover
        transition={Flip}
        pauseOnFocusLoss={false}
        toastStyle={{ cursor: 'pointer' }}
      />
    </div>
  );
};

export default App;